import { useRef, useState } from 'react';
import { Activity, ArrowUp, FileSpreadsheet, FileText, Folder, HardDrive, Link2, MessageSquare, Plus, RefreshCcw, Sparkles, X } from 'lucide-react';
import { activityCountFor, chatStoryFor, filesFor, linkedCountFor, pulsesFor } from '../../data/trackDetail';
import { GROUP_NAMES, TRACKS } from '../../data/workspaceTracks';
import { HUES } from '../alt/hues';
import { Glyph, Panel, Row, RowText } from '../dashboard/ui';
import { TrackActivityView, TrackLinkedView, TrackOverviewView } from './TrackDetailViews';
export type TrackSection = 'chat' | 'overview' | 'linked' | 'activity' | 'pulses' | 'drive';
type ThreadTab = {
  id: number;
  name: string;
  seeded: boolean;
};
const SECTIONS: Array<{
  id: TrackSection;
  name: string;
  icon: typeof MessageSquare;
}> = [{
  id: 'chat',
  name: 'Chat',
  icon: MessageSquare
}, {
  id: 'overview',
  name: 'Overview',
  icon: Folder
}, {
  id: 'linked',
  name: 'Linked',
  icon: Link2
}, {
  id: 'activity',
  name: 'Activity',
  icon: Activity
}, {
  id: 'pulses',
  name: 'Pulses',
  icon: RefreshCcw
}, {
  id: 'drive',
  name: 'Drive',
  icon: HardDrive
}];

/**
 * A TRACK IS A WORKSPACE, NOT A CHATBOX.
 *
 * This component deliberately has no card shell, radius, outer border, inset
 * margin, or drop shadow. It is rendered as a full-height sibling of the chat
 * and workspace views inside Giga Chat, so its boundaries are the browser
 * edges and the shell's own navigation rail. The first implementation put a
 * rounded app inside the app and made the conversation look like a widget.
 *
 * The supplied reference uses a saturated blue track bar. We keep its useful
 * information architecture but use the standard neutral product stack from
 * index.css (--app-* roles): white chrome, near-white canvas, neutral
 * hairlines, near-black type, underline selection. The brand accent marks the
 * active view and the send action, and nothing else.
 */
export function TrackWorkspace({
  trackName,
  initialSection = 'chat',
  staticSection,
  exportMode = false
}: {
  trackName: string;
  /** Initial tab for Screens and direct links; interactive after mount. */
  initialSection?: TrackSection;
  /** Locks the workspace to one finished state for PNG/SVG capture. */
  staticSection?: TrackSection;
  /** Uses a finite artboard height instead of the browser viewport. */
  exportMode?: boolean;
}) {
  const track = TRACKS.find(item => item.name === trackName);
  const group = track?.group ?? GROUP_NAMES[0];
  const paletteKeys = ['indigo', 'teal', 'clay', 'plum', 'sky', 'amber', 'moss'] as const;
  const groupIndex = Math.max(0, GROUP_NAMES.indexOf(group));
  const groupHue = HUES[paletteKeys[groupIndex % paletteKeys.length]];
  const story = chatStoryFor(trackName);
  const trackPulses = pulsesFor(trackName);
  const [interactiveSection, setInteractiveSection] = useState<TrackSection>(initialSection);
  const section = staticSection ?? interactiveSection;
  const [threads, setThreads] = useState<ThreadTab[]>(() => story.threads.map((name, index) => ({
    id: index + 1,
    name,
    seeded: index === 0
  })));
  const [activeThread, setActiveThread] = useState(1);
  const nextThreadId = useRef(story.threads.length + 1);
  const [draft, setDraft] = useState('');
  const active = threads.find(thread => thread.id === activeThread);
  const linkedCount = linkedCountFor(trackName);
  const activityCount = activityCountFor(trackName);
  const pulseCount = trackPulses.length;
  const addThread = () => {
    const id = nextThreadId.current++;
    setThreads(current => [...current, {
      id,
      name: 'New chat',
      seeded: false
    }]);
    setActiveThread(id);
    if (!staticSection) setInteractiveSection('chat');
  };
  const removeThread = (id: number) => {
    setThreads(current => {
      const index = current.findIndex(thread => thread.id === id);
      const next = current.filter(thread => thread.id !== id);
      if (activeThread === id) setActiveThread(next[index - 1]?.id ?? next[0]?.id ?? -1);
      return next;
    });
  };
  return <div className={`flex w-full flex-col bg-app-surface text-app ${exportMode ? 'min-h-[680px]' : 'min-h-screen'}`} data-id="element-3923" data-mp="ozjsclddecf6">
      {/* One product toolbar, not a marketing headline stacked over navigation.
       The track name and its views are peers on the same plane. Colours are
       the semantic --app-* roles, so dark mode is one remap in index.css. */}
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-app bg-app-surface px-4 md:px-6" data-id="element-3924" data-mp="18qotrfw7otif">
        <div className="flex min-w-[180px] max-w-[280px] shrink-0 items-center gap-2.5 pr-3 md:border-r md:border-app" data-id="element-3925" data-mp="1cqfgn8imapho">
          <Folder size={15} className="shrink-0 text-app-subtle" data-id="element-3926" data-mp="ytacxf6dm2pl" />
          <h1 className="truncate text-[14px] font-semibold text-app" data-id="element-3927" data-mp="13ozf7q9s5xpl">{trackName}</h1>
        </div>
        <nav className="flex min-w-0 flex-1 self-stretch gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Track sections" data-id="element-3928" data-mp="3jgncxnwmbu9g">
          {SECTIONS.map(item => {
          const selected = item.id === section;
          const count = item.id === 'chat' ? threads.length : item.id === 'linked' ? linkedCount : item.id === 'activity' ? activityCount : item.id === 'pulses' ? pulseCount : null;
          return <button key={item.id} type="button" onClick={() => {
            if (!staticSection) setInteractiveSection(item.id);
          }} aria-pressed={selected} className={`relative flex shrink-0 items-center gap-1.5 px-3 text-[13px] font-medium transition-colors duration-200 ease-out after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 ${selected ? 'text-app after:bg-app-accent' : 'text-app-subtle after:bg-transparent hover:text-app'}`} data-id="element-3929" data-mp="3pvb0wtae4604">
                <item.icon size={13} strokeWidth={2} data-id="element-3930" data-mp="3271i808h3yd7" />
                {item.name}
                {count !== null && <span className={`text-[11px] tabular-nums ${selected ? 'text-app-muted' : 'text-app-subtle'}`} data-id="element-3931" data-mp="2eh78hrjgapx6">{count}</span>}
              </button>;
        })}
        </nav>
        <span className="hidden shrink-0 items-center gap-1.5 text-[12px] font-medium text-app-muted lg:flex" data-id="element-3932" data-mp="dgsjg8yssro8">
          <span className="size-1.5 rounded-full" style={{
          backgroundColor: groupHue.solid
        }} data-id="element-3933" data-mp="ielnowgjjblv" />
          {group}
        </span>
      </header>

      {/* Open conversations belong to the Track, not only to the Chat view.
       Keeping this strip mounted means the team can inspect Overview or
       Activity without losing the conversations already in progress. */}
      <div className="flex h-11 shrink-0 items-center gap-1 overflow-x-auto border-b border-app bg-app-canvas px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:px-6" role="tablist" aria-label="Open track conversations" data-id="element-3934" data-mp="3vlvtm83mftvi">
        {threads.map(thread => {
        const selected = section === 'chat' && thread.id === activeThread;
        const openThread = () => {
          setActiveThread(thread.id);
          if (!staticSection) setInteractiveSection('chat');
        };
        return <div key={thread.id} role="tab" tabIndex={0} aria-selected={selected} onClick={openThread} onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openThread();
          }
        }} className={`group flex h-8 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-[12px] font-medium transition-colors duration-200 ease-out ${selected ? 'bg-app-surface text-app shadow-[0_1px_2px_rgba(20,20,24,0.08)] ring-1 ring-app' : 'text-app-muted hover:bg-app-hover hover:text-app'}`} data-id="element-3935" data-mp="1jy7cet22cw50">
              <span className="max-w-[160px] truncate" data-id="element-3936" data-mp="2gn9ib1e4yyhn">{thread.name}</span>
              <button type="button" onClick={event => {
            event.stopPropagation();
            removeThread(thread.id);
          }} aria-label={`Close ${thread.name}`} className="text-app-subtle opacity-0 transition-[color,opacity] duration-150 ease-out hover:text-app group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-3937" data-mp="2co8xjdc3lls0">
                <X size={11} data-id="element-3938" data-mp="5uoogkvjl246" />
              </button>
            </div>;
      })}
        <button type="button" onClick={addThread} aria-label="New chat in this track" className="ml-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-app-subtle transition-colors duration-200 ease-out hover:bg-app-hover hover:text-app" data-id="element-3939" data-mp="3axbiq0lg6htm">
          <Plus size={14} data-id="element-3940" data-mp="365tdh0ce5v2u" />
        </button>
      </div>

      {section === 'chat' ? <div className="flex min-h-0 flex-1 flex-col" data-id="element-3941" data-mp="363qhlzok57q7">
          {/* Thread and composer are centred for reading, while their parent is
           full-width. That distinction is the important correction: readable
           content measure does not mean the PRODUCT itself is a card. */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 py-7 md:px-8" data-id="element-3942" data-mp="2ia8j1551c11s">
            {active?.seeded ? <div className="mx-auto flex max-w-3xl flex-col gap-5" data-id="element-3943" data-mp="2n91yh33vu24l">
                <div className="flex justify-end" data-id="element-3944" data-mp="ghkf0tkkh9ky">
                  <div className="max-w-[82%] rounded-2xl rounded-tr-md bg-app-text px-4 py-3 text-[15px] leading-relaxed text-white" data-id="element-3945" data-mp="lgdswn3rn2ln">
                    {story.userPrompt}
                  </div>
                </div>
                <div data-id="element-3946" data-mp="20zuw8gmi4pk0">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[12px] font-medium text-app-subtle" data-id="element-3947" data-mp="210czvybhu61y">
                    <Sparkles size={12} className="text-app-accent" data-id="element-3948" data-mp="2fp0y6ccq4tvl" />
                    Worked for {story.workTime} · {story.toolCalls} tool calls
                  </div>
                  <div className="max-w-[86%] text-[15px] leading-relaxed text-app" data-id="element-3949" data-mp="2on31wznue0mk">
                    {story.assistantIntro}
                  </div>
                </div>
                <div className="max-w-[86%] border-l-2 border-app-accent pl-4" data-id="element-3950" data-mp="2yn3p4wou10w1">
                  <h2 className="text-[14px] font-semibold text-app" data-id="element-3951" data-mp="2tnh9kglj78k2">{story.summaryTitle}</h2>
                  <ul className="mt-2 space-y-2 text-[14px] leading-relaxed text-app-muted" data-id="element-3952" data-mp="148j9iyfdbvji">
                    {story.highlights.map(highlight => <li key={highlight} data-id="element-3953" data-mp="za7zonp2pl1a">{highlight}</li>)}
                  </ul>
                </div>
              </div> : <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center text-center" data-id="element-3954" data-mp="3tpr7jprparov">
                <MessageSquare size={22} className="text-app-subtle" data-id="element-3955" data-mp="3ootsvn5wgfs4" />
                <p className="mt-3 text-[14px] text-app-muted" data-id="element-3956" data-mp="3jq0d1o354cos">
                  New chat in {trackName}. Ask Giga to get something done.
                </p>
              </div>}
          </div>

          <div className="shrink-0 bg-app-canvas px-5 py-4 md:px-8" data-id="element-3957" data-mp="gacrozeaf5uk">
            <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-app bg-app-surface px-4 py-3 shadow-[0_8px_30px_-20px_rgba(20,20,24,0.2)] focus-within:border-app-strong" data-id="element-3958" data-mp="gbnw91fk4hmq">
              <button type="button" aria-label="Add attachment" className="flex size-7 shrink-0 items-center justify-center text-app-subtle transition-colors duration-200 ease-out hover:text-app" data-id="element-3959" data-mp="6hnk4fluyc4i">
                <Plus size={17} data-id="element-3960" data-mp="z25mmzcx314x" />
              </button>
              <textarea value={draft} onChange={event => setDraft(event.target.value)} rows={1} placeholder={`Message Giga about ${trackName}`} className="min-h-[24px] max-h-28 flex-1 resize-none bg-transparent text-[15px] text-app outline-none placeholder:text-app-subtle" data-id="element-3961" data-mp="u8sxmo4bssbn" />
              <button type="button" aria-label="Send" disabled={!draft.trim()} className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-app-accent text-white transition-colors duration-200 ease-out enabled:hover:bg-[var(--app-accent-strong)] disabled:opacity-25" data-id="element-3962" data-mp="1bidnf3dpcsoh">
                <ArrowUp size={15} data-id="element-3963" data-mp="1pig26gqpr5zi" />
              </button>
            </div>
            <p className="mt-2 text-center text-[11.5px] text-app-subtle" data-id="element-3964" data-mp="1pgleiuo24zrq">
              Giga can make mistakes. Check important information.
            </p>
          </div>
        </div> : <TrackSectionContent section={section} trackName={trackName} group={group} groupHue={groupHue} data-id="element-3965" data-mp="1z7zipfqyenr7" />}
    </div>;
}
function TrackSectionContent({
  section,
  trackName,
  group,
  groupHue
}: {
  section: Exclude<TrackSection, 'chat'>;
  trackName: string;
  group: string;
  groupHue: {
    solid: string;
    tint: string;
    ink: string;
  };
}) {
  const detailProps = {
    trackName,
    group,
    groupHue
  };
  return <div className="min-h-0 flex-1 overflow-y-auto bg-app-canvas px-5 py-8 md:px-8" data-id="element-3966" data-mp="1ehlxfiwxn5ks">
      {section === 'overview' && <TrackOverviewView {...detailProps} data-id="element-3967" data-mp="1ohwbf5qskh10" />}
      {section === 'linked' && <TrackLinkedView {...detailProps} data-id="element-3968" data-mp="1ofjjbzrbkqa8" />}
      {section === 'activity' && <TrackActivityView {...detailProps} data-id="element-3969" data-mp="227pzvmydwza7" />}
      {section === 'pulses' && <TrackPulses trackName={trackName} data-id="element-3970" data-mp="1xag3toghppde" />}
      {section === 'drive' && <TrackDrive trackName={trackName} data-id="element-3971" data-mp="1xcankzg0zbfw" />}
    </div>;
}
function TrackPulses({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-3972" data-mp="278droauftk5h">
      <div className="mb-5" data-id="element-3973" data-mp="278nhnyfhfrb6">
        <h2 className="text-[16px] font-semibold text-app" data-id="element-3974" data-mp="2769o5u0gan5q">Work running on its own</h2>
        <p className="mt-1 text-[13.5px] text-app-muted" data-id="element-3975" data-mp="hva7vtolpb2o">Giga watches this track and delivers updates before the team has to ask.</p>
      </div>
      <Panel data-id="element-3976" data-mp="2ogwtjl1p93j6">
        {pulsesFor(trackName).map(pulse => <Row key={pulse.name} data-id="element-3977" data-mp="2oi84c4tkbbjs">
            <Glyph icon={RefreshCcw} data-id="element-3978" data-mp="2tg9q4xidhoyy" />
            <RowText title={pulse.name} meta={`${pulse.meta} · ${pulse.outcome}`} data-id="element-3979" data-mp="2tf81trccj5za" />
            <span className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold ${pulse.status === 'Running' ? 'bg-app-accent-tint text-app-accent' : 'bg-app-subtle text-app-muted'}`} data-id="element-3980" data-mp="2eseup9czd6i6">{pulse.status}</span>
          </Row>)}
      </Panel>
    </div>;
}
function TrackDrive({
  trackName
}: {
  trackName: string;
}) {
  return <div className="mx-auto max-w-[900px]" data-id="element-3981" data-mp="29tbo3khcqs83">
      <div className="mb-5" data-id="element-3982" data-mp="1vsr62qj9h0fn">
        <h2 className="text-[16px] font-semibold text-app" data-id="element-3983" data-mp="1vtkh43p96c3l">Files and deliverables</h2>
        <p className="mt-1 text-[13.5px] text-app-muted" data-id="element-3984" data-mp="20rm24u68olhp">Generated outputs and source files stay with the work that produced them.</p>
      </div>
      <Panel data-id="element-3985" data-mp="3q7blanotc3rn">
        {filesFor(trackName).map(file => <Row key={file.name} data-id="element-3986" data-mp="3v5muqxb74c9c">
            <Glyph icon={file.kind === 'spreadsheet' ? FileSpreadsheet : FileText} data-id="element-3987" data-mp="3v4tv0ganmn0p" />
            <RowText title={file.name} meta={file.meta} data-id="element-3988" data-mp="1v3vsb2gdiadm" />
          </Row>)}
      </Panel>
    </div>;
}